import React, { useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Alert, Box, Button, CircularProgress, IconButton, Paper, Stack, Typography } from "@mui/material"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import CloseIcon from "@mui/icons-material/Close"
import FileOpenIcon from "@mui/icons-material/FileOpen"

import type { SkuCostConfig } from "@/features/profit-calculators/calculator-shared/types"
import type { AmazonOrderRow, AmazonTransactionRow } from "@/features/profit-calculators/amazon-calculator/types"
import { useMonthlyReports } from "@/features/profit-calculators/calculator-shared/hooks/useMonthlyReports"
import {
  validateAmazonMonth,
  validateAmazonTransactionMonth
} from "@/features/profit-calculators/amazon-calculator/utils/monthValidation"
import {
  computeAmazonCombinedReport,
  parseAmazonAllOrders,
  parseAmazonTransactions
} from "@/features/profit-calculators/amazon-calculator/utils/amazonReports"
import {
  buildInitialSkuCosts,
  CalculatorWizardHeader,
  loadSkuCosts,
  monthKeyToLabel,
  persistSkuCosts,
  SkuCostsStep,
  type UniqueSku
} from "@/features/profit-calculators/calculator-shared/components/CalculatorWizardShared"
import SkuCostModal from "@/features/profit-calculators/calculator-shared/components/SkuCostModal"
import WizardDropZone from "@/features/profit-calculators/calculator-shared/components/WizardDropZone"
import { getReportMonthGenerationError } from "@/features/profit-calculators/calculator-shared/utils/reportMonthUtils"

const STEPS = ["Upload Orders Report", "Configure SKU Costs", "Upload Transaction CSV", "Generate Report"]
const SKU_STORAGE_KEY = "amazon_sku_costs"

const AmazonCalculatorPage: React.FC = () => {
  const { monthKey = "" } = useParams<{ monthKey: string }>()
  const navigate = useNavigate()
  const targetMonthLabel = useMemo(() => monthKeyToLabel(monthKey), [monthKey])
  const reportMonthError = useMemo(() => getReportMonthGenerationError(monthKey), [monthKey])
  const reportYear = Number(monthKey.slice(0, 4)) || new Date().getFullYear()
  const { saveReport, reportExists } = useMonthlyReports("amazon", reportYear)

  const [activeStep, setActiveStep] = useState(0)
  const [ordersFileName, setOrdersFileName] = useState("")
  const [ordersSourceFile, setOrdersSourceFile] = useState<File | null>(null)
  const [orders, setOrders] = useState<AmazonOrderRow[]>([])
  const [uniqueSkus, setUniqueSkus] = useState<UniqueSku[]>([])
  const [skuCosts, setSkuCosts] = useState<Record<string, SkuCostConfig>>(() => loadSkuCosts(SKU_STORAGE_KEY))
  const [costsConfigured, setCostsConfigured] = useState(false)
  const [skuModalOpen, setSkuModalOpen] = useState(false)
  const [transactionFileName, setTransactionFileName] = useState("")
  const [transactionSourceFile, setTransactionSourceFile] = useState<File | null>(null)
  const [transactions, setTransactions] = useState<AmazonTransactionRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)
  const [generateError, setGenerateError] = useState<string | null>(null)

  const monthReports = useMemo(
    () =>
      orders.length > 0 && transactions.length > 0 ? computeAmazonCombinedReport(orders, transactions, skuCosts) : null,
    [orders, transactions, skuCosts]
  )

  const persistReport = async () => {
    setGenerateError(null)
    if (!monthReports || !ordersSourceFile || !transactionSourceFile) {
      setGenerateError("The uploaded source files are no longer available. Please upload them again.")
      return
    }

    const validation = validateAmazonMonth(
      orders.map(order => order.purchaseDate || ""),
      targetMonthLabel
    )
    if (!validation.valid) {
      setGenerateError(validation.reason || "This upload does not match the selected month.")
      return
    }

    const transactionValidation = validateAmazonTransactionMonth(
      transactions.map(transaction => transaction.dateTime || ""),
      targetMonthLabel
    )
    if (!transactionValidation.valid) {
      setGenerateError(transactionValidation.reason || `Please upload the ${targetMonthLabel} transaction CSV.`)
      return
    }

    const report = monthReports[targetMonthLabel]
    if (!report) {
      setGenerateError(
        `No ${targetMonthLabel} data was found in the uploaded files. Please check the files and try again.`
      )
      return
    }

    setGenerating(true)
    try {
      await saveReport(monthKey, report, [
        { role: "orders", file: ordersSourceFile },
        { role: "transactions", file: transactionSourceFile }
      ])
      navigate(`/amazon-calculator/${monthKey}/report`)
    } catch (err) {
      setGenerateError(err instanceof Error ? err.message : "Failed to save the report. Please try again.")
    } finally {
      setGenerating(false)
    }
  }

  const handleGenerateReport = () => {
    setGenerateError(null)
    if (reportMonthError) {
      setGenerateError(reportMonthError)
      return
    }
    if (reportExists(monthKey)) {
      setGenerateError("This report has already been generated and cannot be updated or deleted.")
      return
    }
    persistReport()
  }

  const handleOrdersLoaded = async (file: File) => {
    setLoading(true)
    setError(null)
    try {
      const lowerName = file.name.toLowerCase()
      if (!lowerName.endsWith(".txt") && !lowerName.endsWith(".tsv")) {
        throw new Error("Invalid file format. Please upload a tab-separated text/tsv file (.txt/.tsv).")
      }

      const result = await parseAmazonAllOrders(file)
      if (result.orders.length === 0) {
        throw new Error("No orders found in the file. Please check if the file format is correct.")
      }

      const validation = validateAmazonMonth(
        result.orders.map(order => order.purchaseDate || ""),
        targetMonthLabel
      )
      if (!validation.valid) throw new Error(validation.reason || `Please upload ${targetMonthLabel} files.`)

      setOrdersFileName(file.name)
      setOrdersSourceFile(file)
      setOrders(result.orders)
      setUniqueSkus(result.uniqueSkus)
      setSkuCosts(buildInitialSkuCosts(result.uniqueSkus, SKU_STORAGE_KEY))
      setActiveStep(1)
      setSkuModalOpen(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to parse file.")
    } finally {
      setLoading(false)
    }
  }

  const handleTransactionsLoaded = async (file: File) => {
    setLoading(true)
    setError(null)
    try {
      if (!file.name.toLowerCase().endsWith(".csv")) {
        throw new Error("Invalid file format. Please upload a .csv file.")
      }
      const rows = await parseAmazonTransactions(file)
      if (rows.length === 0) throw new Error("No transaction entries found in the CSV file.")

      const validation = validateAmazonTransactionMonth(
        rows.map(transaction => transaction.dateTime || ""),
        targetMonthLabel
      )
      if (!validation.valid) {
        throw new Error(validation.reason || `Please upload the ${targetMonthLabel} transaction CSV.`)
      }

      setTransactionFileName(file.name)
      setTransactionSourceFile(file)
      setTransactions(rows)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to parse file.")
    } finally {
      setLoading(false)
    }
  }

  const handleSaveCosts = (costs: Record<string, SkuCostConfig>) => {
    setSkuCosts(costs)
    setCostsConfigured(true)
    persistSkuCosts(SKU_STORAGE_KEY, costs)
  }

  const handleRemoveOrders = () => {
    setOrdersFileName("")
    setOrdersSourceFile(null)
    setOrders([])
    setUniqueSkus([])
    setSkuCosts({})
    setCostsConfigured(false)
    setActiveStep(0)
  }

  if (reportMonthError) {
    return (
      <Stack spacing={2} sx={{ pb: 6 }}>
        <Alert severity="error">{reportMonthError}</Alert>
        <Box>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate("/amazon-calculator")}>
            Back to Amazon Reports
          </Button>
        </Box>
      </Stack>
    )
  }

  return (
    <Box sx={{ pb: 6 }}>
      <CalculatorWizardHeader
        activeStep={activeStep}
        finalStep={3}
        steps={STEPS}
        platformName="Amazon"
        error={error}
        generateError={generateError}
        generating={generating}
        onClearError={() => setError(null)}
        onClearGenerateError={() => setGenerateError(null)}
        onRetryGenerate={handleGenerateReport}
      />

      {activeStep === 0 && (
        <Stack spacing={3}>
          <Typography variant="h6" fontWeight={750}>
            Step 1: Upload Amazon Orders Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Download All Orders report from: <strong>Seller Central → Reports → Fulfillment → All Orders</strong>. This
            is a tab-separated text/tsv file containing SKUs.
          </Typography>
          <WizardDropZone
            accept=".txt,.tsv"
            title={`Upload Amazon Orders Report for month of ${targetMonthLabel}`}
            subtitle={`Drag & drop or click to browse (only .txt/.tsv accepted) • Selected month: ${targetMonthLabel}`}
            loading={loading}
            onFileSelected={handleOrdersLoaded}
            icon={<FileOpenIcon sx={{ fontSize: 40, color: "primary.main" }} />}
          />
        </Stack>
      )}

      {activeStep === 1 && (
        <SkuCostsStep
          fileName={ordersFileName}
          orderCount={orders.length}
          uniqueSkuCount={uniqueSkus.length}
          costsConfigured={costsConfigured}
          nextStepLabel="Upload Transaction CSV"
          onConfigure={() => setSkuModalOpen(true)}
          onRemoveFile={handleRemoveOrders}
          onBack={() => setActiveStep(0)}
          onNext={() => setActiveStep(2)}
        />
      )}

      {activeStep === 2 && (
        <Stack spacing={3}>
          <Typography variant="h6" fontWeight={750}>
            Step 3: Upload Custom Unified Transaction CSV
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Download the report from:{" "}
            <strong>Seller Central → Payments → Reports → Custom Unified Transaction CSV</strong>.
          </Typography>

          {transactions.length > 0 ? (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: "rgba(34, 197, 94, 0.04)",
                borderColor: "success.main"
              }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={2} alignItems="center">
                  <CheckCircleIcon sx={{ color: "success.main", fontSize: 28 }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Transaction CSV Loaded Successfully
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      File: <code>{transactionFileName}</code>
                    </Typography>
                  </Box>
                </Stack>
                <IconButton
                  onClick={() => {
                    setTransactionFileName("")
                    setTransactionSourceFile(null)
                    setTransactions([])
                  }}
                  color="error"
                  title="Remove file">
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Paper>
          ) : (
            <WizardDropZone
              accept=".csv"
              title={`Upload Custom Unified Transaction CSV for ${targetMonthLabel}`}
              subtitle={`Drag & drop or click to browse (only .csv accepted) • Selected month: ${targetMonthLabel}`}
              loading={loading}
              onFileSelected={handleTransactionsLoaded}
              icon={<FileOpenIcon sx={{ fontSize: 40, color: "warning.main" }} />}
            />
          )}

          <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => setActiveStep(1)}
              sx={{ borderRadius: 2 }}>
              Back
            </Button>
            <Button
              variant="contained"
              disabled={transactions.length === 0 || generating || reportExists(monthKey)}
              onClick={handleGenerateReport}
              color="success"
              sx={{ borderRadius: 2 }}>
              {generating ? (
                <CircularProgress size={20} sx={{ color: "white" }} />
              ) : reportExists(monthKey) ? (
                "Report Already Generated"
              ) : (
                "Generate Profit Report"
              )}
            </Button>
          </Stack>
        </Stack>
      )}

      {skuModalOpen && (
        <SkuCostModal
          open={skuModalOpen}
          onClose={() => setSkuModalOpen(false)}
          uniqueSkus={uniqueSkus}
          initialCosts={skuCosts}
          onSave={handleSaveCosts}
        />
      )}
    </Box>
  )
}

export default AmazonCalculatorPage
